import React from 'react';
import { useNavigate } from 'react-router';
import { Points } from 'components/Points/Points';
import RankTierBadge from '../RankTierBadge';
import {
  CATEGORY_CONFIG,
  LEADERBOARD_GROUPS,
  LeaderboardCategory,
  LeaderboardGroup,
  LeaderboardPeriod,
  LeaderboardStandingBoard,
  LeaderboardStandingResponse,
} from 'types/interfaces/leaderboard';
import { GapLine, standingBarWidth, standingGapLine } from '../../utils/leaderboardDisplay';
import './YourStandingPanel.css';

/** Renders a gap line; a points gap goes through the branded Points component. */
export const GapText: React.FC<{ line: GapLine }> = ({ line }) => {
  if (line.text) return <>{line.text}</>;
  return (
    <>
      {line.before}
      {line.gapPoints != null ? <Points points={line.gapPoints} type="inline" /> : line.gap}
      {line.after}
    </>
  );
};

interface YourStandingPanelProps {
  group: LeaderboardGroup;
  period: LeaderboardPeriod;
  isLoggedIn: boolean;
  /** The latest standing result, which may still be for the previous group or period while the next loads. */
  standing?: LeaderboardStandingResponse;
  activeCategory: LeaderboardCategory;
  onSelectBoard: (category: LeaderboardCategory) => void;
}

/**
 * The viewer's rank, gap and progress on every board in the active group.
 *
 * Rows are keyed by POSITION, not by board, so the fill element stays mounted
 * across group / board / period changes and its width transition actually
 * runs (a remount would jump straight to the new width).
 */
const YourStandingPanel: React.FC<YourStandingPanelProps> = ({
  group,
  period,
  isLoggedIn,
  standing,
  activeCategory,
  onSelectBoard,
}) => {
  const navigate = useNavigate();
  const groupConfig = LEADERBOARD_GROUPS.find((g) => g.key === group) ?? LEADERBOARD_GROUPS[0];

  if (!isLoggedIn) {
    return (
      <section className="your-standing your-standing--guest" aria-label="Your standing">
        <div className="your-standing__cta-message">
          <span aria-hidden="true">&#128100;</span>
          <span>Log in to see your rank on the leaderboard</span>
        </div>
        <button type="button" className="your-standing__cta" onClick={() => navigate('/login')}>
          Log In
        </button>
      </section>
    );
  }

  // While the next group / period loads, `standing` still holds the previous
  // result. Its numbers are not shown under the new labels; it only keeps each
  // slot's bar at its old width, so the bar slides to the new width when the
  // result lands instead of dropping to zero first.
  const isFresh = !!standing && standing.group === group && standing.period === period;
  const boards: LeaderboardStandingBoard[] = groupConfig.boards.map(
    (category) =>
      (isFresh ? standing!.boards.find((b) => b.category === category) : undefined) ?? {
        category,
        rank: null,
        displayValue: 0,
        above: null,
      }
  );
  const widthFor = (slot: number): number => {
    if (isFresh) return standingBarWidth(boards[slot]);
    const previous = standing?.boards[slot];
    return previous ? standingBarWidth(previous) : 0;
  };
  // Zero-trade state: nothing ranked on any board in the group yet (the joined
  // date on New Members is not a rank).
  const hasNoRanks = isFresh && boards.every((b) => b.category === 'new_members' || b.rank === null);

  return (
    <section className="your-standing" aria-label="Your standing">
      <header className="your-standing__header">
        <span className="your-standing__title">Your standing &middot; {groupConfig.label}</span>
        {standing && <RankTierBadge tier={standing.tier} size="small" />}
      </header>

      {hasNoRanks && (
        <div className="your-standing__empty">
          <span>Start trading to appear on the leaderboard!</span>
          <button type="button" className="your-standing__cta" onClick={() => navigate('/sending')}>
            Start Trading
          </button>
        </div>
      )}

      <ul className="your-standing__rows">
        {boards.map((board, index) => {
          const isNewMembers = board.category === 'new_members';
          const width = widthFor(index);
          return (
            <li key={`slot-${index}`}>
              <button
                type="button"
                className={`your-standing__row${board.category === activeCategory ? ' your-standing__row--active' : ''}`}
                onClick={() => onSelectBoard(board.category)}
              >
                <span className="your-standing__row-head">
                  <span className="your-standing__label">{CATEGORY_CONFIG[board.category].label}</span>
                  {!isNewMembers && (
                    <span className="your-standing__rank">
                      {!isFresh ? ' ' : board.rank === null ? '–' : `#${board.rank}`}
                    </span>
                  )}
                </span>
                {!isNewMembers && (
                  <span className="your-standing__bar" aria-hidden="true">
                    <span className="your-standing__bar-fill" style={{ width: `${width}%` }} />
                  </span>
                )}
                <span className="your-standing__gap">
                  {isFresh ? <GapText line={standingGapLine(board)} /> : ' '}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default YourStandingPanel;
